<template>
  <Transition name="page-loading">
    <div
      v-if="visible"
      class="tw-fixed tw-top-0 tw-left-0 tw-flex tw-flex-col tw-justify-center tw-items-center tw-w-full tw-h-full tw-bg-white tw-text-center tw-z-9999"
    >
      <MediaImageLazyLoad
        :src="websiteInfo?.logo?.large || ''"
        :alt="websiteInfo?.name"
        :class=customLoadingPicSize
        fit="contain"
        seo-optimize
      />
      <div class="fast-loading tw-mt-4 tw-flex tw-gap-x-0.5">
        <div class="fast-bounce1 fast-bounce" />
        <div class="fast-bounce2 fast-bounce" />
        <div class="fast-bounce" />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
const statusStore = useStatusStore()
const websiteInfo = useWebsiteInfoStore()

defineProps({
  customLoadingPicSize: {
    type: String,
    default: 'tw-w-24 tw-h-24'
  }
})

const SHOW_DELAY = 150   // ms — don't show if content loads faster than this
const MIN_VISIBLE = 350  // ms — minimum time to stay visible once shown

const visible = ref(false)
let showTimer: ReturnType<typeof setTimeout> | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null
let shownAt = 0

watch(() => statusStore.preloading, (isLoading) => {
  if (isLoading) {
    // Cancel any pending hide — loading resumed before it finished
    if (hideTimer) {
      clearTimeout(hideTimer)
      hideTimer = null
    }
    if (showTimer) return
    showTimer = setTimeout(() => {
      visible.value = true
      shownAt = Date.now()
      showTimer = null
    }, SHOW_DELAY)
  } else {
    if (showTimer) {
      clearTimeout(showTimer)
      showTimer = null
    }
    if (visible.value && !hideTimer) {
      const elapsed = Date.now() - shownAt
      const remaining = MIN_VISIBLE - elapsed
      hideTimer = setTimeout(() => {
        visible.value = false
        hideTimer = null
      }, remaining > 0 ? remaining : 0)
    }
  }
})
</script>

<style lang="scss" scoped>
  .page-loading-enter-active {
    transition: opacity 0.15s ease;
  }

  .page-loading-leave-active {
    transition: opacity 0.3s ease;
  }

  .page-loading-enter-from,
  .page-loading-leave-to {
    opacity: 0;
  }

  .fast-loading .fast-bounce {
    background-color: var(--wft-primary-color);
  }

  .fast-loading {
    @apply tw-text-center;
  }

  .fast-loading > div {
    @apply tw-w-3 tw-h-3 tw-rounded-full tw-inline-block;
    -webkit-animation: bounce-delay 1.4s infinite ease-in-out both;
    animation: bounce-delay 1.4s infinite ease-in-out both;
  }

  .fast-loading .fast-bounce1 {
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
  }

  .fast-loading .fast-bounce2 {
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
  }

  @-webkit-keyframes bounce-delay {
    0%, 80%, 100% { -webkit-transform: scale(0) }
    40% { -webkit-transform: scale(1.0) }
  }

  @keyframes bounce-delay {
    0%, 80%, 100% {
        -webkit-transform: scale(0);
        transform: scale(0);
    } 40% {
        -webkit-transform: scale(1.0);
        transform: scale(1.0);
    }
  }
</style>
